import React, { useCallback, useEffect, useRef, useState } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, getFns } from '../firebase'
import Icon from './AppIcon'
import Button from './ui/Button'
import Card from './ui/Card'

const STATUS = {
  allowed: { label: 'Store network matched', description: 'This connection matches a saved store internet address.', icon: 'CheckCircle2', color: 'text-success' },
  outside_store: { label: 'Store network not matched', description: 'Connect to the store Wi-Fi and check again. Contact an administrator if this continues.', icon: 'WifiOff', color: 'text-warning' },
  not_configured: { label: 'Store network not configured', description: 'An administrator needs to save the store internet addresses in Settings.', icon: 'Settings', color: 'text-muted-foreground' },
  unavailable: { label: 'Network could not be verified', description: 'This connection has not been approved. Check again when the service is available.', icon: 'CircleHelp', color: 'text-muted-foreground' },
}

export function StoreNetworkResult({ result, checking = false }) {
  const status = STATUS[result?.status] || STATUS.unavailable
  const checkedAt = result?.checkedAt ? new Date(result.checkedAt) : null
  const validDate = checkedAt && !Number.isNaN(checkedAt.getTime())

  return <div className="flex min-w-0 items-start gap-3" role="status" aria-live="polite">
    <Icon name={checking ? 'LoaderCircle' : status.icon} size={19} className={`mt-0.5 shrink-0 ${checking ? 'animate-spin text-muted-foreground' : status.color}`} />
    <div className="min-w-0">
      <p className="text-sm font-medium text-foreground">{checking ? 'Checking store network…' : status.label}</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{checking ? 'Verifying this connection with the server.' : status.description}</p>
      {!checking && validDate && <p className="mt-1.5 text-[11px] text-muted-foreground">Last checked <time dateTime={result.checkedAt}>{checkedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time></p>}
    </div>
  </div>
}

export default function StoreNetworkStatus({ className = '' }) {
  const [result, setResult] = useState(null)
  const [checking, setChecking] = useState(true)
  const [signedIn, setSignedIn] = useState(false)
  const requestRef = useRef(0)

  const checkNetwork = useCallback(async () => {
    const request = ++requestRef.current
    const user = auth.currentUser
    if (!user) {
      setResult(null)
      setChecking(false)
      return
    }
    if (!navigator.onLine) {
      setResult({ status: 'unavailable' })
      setChecking(false)
      return
    }
    setChecking(true)
    setResult(null)
    try {
      const { data } = await httpsCallable(getFns(), 'storeNetworkCheck')({})
      if (request === requestRef.current && auth.currentUser?.uid === user.uid) setResult(data)
    } catch {
      if (request === requestRef.current) setResult({ status: 'unavailable' })
    } finally {
      if (request === requestRef.current) setChecking(false)
    }
  }, [])

  useEffect(() => {
    const invalidate = () => {
      ++requestRef.current
      setResult({ status: 'unavailable' })
      setChecking(false)
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkNetwork()
      else invalidate()
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setSignedIn(Boolean(user))
      checkNetwork()
    })
    window.addEventListener('offline', invalidate)
    window.addEventListener('online', checkNetwork)
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      unsubscribe()
      window.removeEventListener('offline', invalidate)
      window.removeEventListener('online', checkNetwork)
      document.removeEventListener('visibilitychange', handleVisibility)
      ++requestRef.current
    }
  }, [checkNetwork])

  return <Card padding="sm" className={className} aria-label="Store network">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <StoreNetworkResult result={result} checking={checking} />
      <Button type="button" variant="outline" size="sm" iconName="RefreshCw" disabled={!signedIn} loading={checking} onClick={checkNetwork} className="shrink-0 self-start">Check network</Button>
    </div>
    <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">A network match identifies the internet connection. It does not confirm your physical location.</p>
  </Card>
}
